import React, { useState, useEffect, useCallback } from 'react';
import './DealDetail.css';
import BackToTopButton from './BackToTopButton';

const DealDetail = ({ deal, user, goBack, handleDealRedirect, navigateTo }) => {
  const [organization, setOrganization] = useState(null);
  const [organizationError, setOrganizationError] = useState('');
  const [categorySuggestions, setCategorySuggestions] = useState([]);
  const [showImagePopup, setShowImagePopup] = useState(false);
  const [popupImage, setPopupImage] = useState('');
  const [loading, setLoading] = useState(true);

  const defaultImage = 'https://via.placeholder.com/400x300.png?text=Image+Not+Available';

  // Memoize fetchOrganization to prevent re-creation on each render
  const fetchOrganization = useCallback(async (signal) => {
    if (!deal || !deal.organizationId) return;
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/organizations/${deal.organizationId}`, {
        headers: { 'Authorization': `Bearer ${user?.token || ''}` },
        signal,
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Organization not found');
      }
      const data = await response.json();
      setOrganization(data);
      setOrganizationError('');
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('Error fetching organization:', err);
        setOrganizationError('Organization not found in the database.');
        setOrganization(null);
      }
    }
  }, [deal?.organizationId, user?.token]); // Stable dependencies

  // Memoize fetchCategories to prevent re-creation on each render
  const fetchCategories = useCallback(async (signal) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/categories`, {
        headers: { 'Authorization': `Bearer ${user?.token || ''}` },
        signal,
      });
      const data = await response.json();
      if (response.ok) {
        const allCategories = data
          .map(category => ({
            value: category._id,
            label: category.name,
            icon: category.IconURL,
          }))
          .sort((a, b) => a.label.localeCompare(b.label));
        setCategorySuggestions(allCategories);
      } else {
        throw new Error('Failed to fetch categories');
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('Error fetching category suggestions:', err);
        setCategorySuggestions([]);
      }
    }
  }, [user?.token]); // Stable dependency

  useEffect(() => {
    const abortController = new AbortController();
    const signal = abortController.signal;

    const loadData = async () => {
      setLoading(true);
      setOrganizationError('');
      setCategorySuggestions([]);
      try {
        await Promise.all([
          fetchOrganization(signal),
          fetchCategories(signal),
        ]);
      } finally {
        setLoading(false);
      }
    };

    if (deal && deal._id && user && user.token) {
      loadData();
    } else {
      setLoading(false);
    }

    return () => {
      abortController.abort(); // Cleanup: abort ongoing fetch requests
    };
  }, [deal?._id, user?.token, fetchOrganization, fetchCategories]); // Stable dependencies

  const handleImageClick = (imageUrl) => {
    setPopupImage(imageUrl);
    setShowImagePopup(true);
  };

  const handleClosePopup = () => {
    setShowImagePopup(false);
    setPopupImage('');
  };

  const handleEdit = () => {
    navigateTo('dealForm', { deal, isEdit: true });
  };

  if (loading) {
    return <div className="deal-details"><p>Loading...</p></div>;
  }

  if (!deal || !deal._id) {
    return <div className="deal-details"><p>No deal found.</p></div>;
  }

  const organizationLocation = organization?.location || { lat: -26.2041, lng: 28.0473 };
  const mapUrl = `https://www.google.com/maps/embed/v1/place?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&q=${organizationLocation.lat},${organizationLocation.lng}&zoom=15`;

  return (
    <div className="deal-details">
      <div className="deal-header">
        <h1 className="deal-header-title">{deal.title}</h1>
        <p className="deal-header-price">
          Starting from R {deal.price ? deal.price.toFixed(2) : 'N/A'}
        </p>
        <button
          className="visit-deal-button"
          onClick={() => handleDealRedirect(deal._id, deal.link)}
          disabled={true}
        >
          Visit Deal
        </button>
      </div>

      <div className="deal-details-container">
        <div className="deal-details-main">
          <div className="deal-content-wrapper">
            <div className="deal-image-wrapper">
              <img
                src={deal.image || defaultImage}
                alt={deal.title}
                className="deal-details-image"
                onClick={() => handleImageClick(deal.image || defaultImage)}
                loading="lazy"
              />
              <div className="deal-image-overlay">
                <span>Click to Enlarge</span>
              </div>
            </div>
            <div className="deal-info-content">
              <div className="deal-info-section">
                <h3 className="section-title">Description</h3>
                <p className="deal-description">{deal.description}</p>
              </div>

              <div className="deal-info-section">
                <h3 className="section-title">Categories</h3>
                <div className="deal-categories">
                  {deal.category.map((cat, index) => {
                    const category = categorySuggestions.find(c => c.value === (typeof cat === 'object' ? cat._id : cat));
                    return category ? (
                      <div key={index} className="category-item">
                        {category.icon && <img src={category.icon} alt={category.label} className="deal-category-icon" loading="lazy" />}
                        <span>{category.label}</span>
                      </div>
                    ) : null;
                  })}
                </div>
              </div>

              <div className="deal-info-section">
                <h3 className="section-title">Validity Period</h3>
                <p>
                  Valid from {new Date(deal.startDate).toLocaleDateString('en-GB')} to {new Date(deal.endDate).toLocaleDateString('en-GB')}
                </p>
              </div>

              <div className="deal-info-section">
                <h3 className="section-title">Available Days</h3>
                <p>{deal.dayOfWeek.join(', ')}</p>
              </div>

              <div className="deal-info-section">
                <h3 className="section-title">Types</h3>
                <p>{deal.type.length > 0 ? deal.type.join(', ') : 'None'}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="deal-details-sidebar">
          {organizationError ? (
            <p className="error">{organizationError}</p>
          ) : organization ? (
            <>
              <div className="organization-header">
                {organization.logo && (
                  <img
                    src={organization.logo}
                    alt={`${organization.companyName} Logo`}
                    className="organization-logo"
                    loading="lazy"
                  />
                )}
                <h2 className="organization-name">{organization.companyName}</h2>
              </div>
              <div className="organization-info">
                <p><span className="info-label">Address:</span> {organization.businessAddress}</p>
                <p><span className="info-label">Contact:</span>
                  {organization.contactNumber ? `(${organization.countryCode || '+27'}) ${organization.contactNumber}` : 'No contact available'}
                </p>
              </div>
              <div className="map-container">
                <h3 className="section-title">Location</h3>
                <iframe
                  title="Organization Location"
                  className="map-iframe"
                  src={mapUrl}
                  allowFullScreen
                  loading="lazy"
                />
              </div>
            </>
          ) : (
            <p>Unable to load organization details.</p>
          )}
        </div>
      </div>

      <div className="button-container">
        <button className="back-button" onClick={goBack}>Back</button>
        {user.role !== 'Light' && (
          <button className="edit-button" onClick={handleEdit}>Edit</button>
        )}
      </div>

      {showImagePopup && (
        <div className="image-popup">
          <div className="image-popup-content">
            <button className="image-popup-close" onClick={handleClosePopup}>
              ✕
            </button>
            <img src={popupImage} alt={deal.title} className="popup-image" />
          </div>
        </div>
      )}

      <BackToTopButton />
    </div>
  );
};

export default DealDetail;